// React Query wiring for graph re-grounding (evidence re-attachment).
//
// The report is a deterministic-shaped proposal computed on demand: generate
// runs a sync forced-tool Bedrock call (GET /reattach reads the latest), and
// apply/dismiss curate it. apply moves a market_signal/assumption edge off the
// idea-fallback onto the node it bears on — that changes the graph, so it
// invalidates the canvas and the alignment report (the report leans on direct
// idea-attachment for the fragile-assumption signal).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { ReattachRow } from '@/types/api';

export function useReattach(projectId: string) {
  return useQuery<ReattachRow | null>({
    queryKey: ['reattach', projectId],
    queryFn: async () => {
      const res = await api.getReattach(projectId);
      if (!res.success) throw new Error(res.message || 'Failed to load re-grounding');
      return res.data ?? null;
    },
    enabled: !!projectId,
  });
}

export function useGenerateReattach(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ReattachRow, Error, void>({
    mutationFn: async () => {
      const res = await api.generateReattach(projectId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to scan for re-grounding');
      return res.data;
    },
    onSuccess: (row) => {
      queryClient.setQueryData(['reattach', projectId], row);
    },
  });
}

export function useApplyReattach(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (proposalId) => {
      const res = await api.applyReattach(projectId, proposalId);
      if (!res.success) throw new Error(res.message || 'Failed to re-attach evidence');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reattach', projectId] });
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      queryClient.invalidateQueries({ queryKey: ['alignment', projectId] });
    },
  });
}

export function useDismissReattach(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (proposalId) => {
      const res = await api.dismissReattach(projectId, proposalId);
      if (!res.success) throw new Error(res.message || 'Failed to dismiss');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reattach', projectId] });
    },
  });
}
